import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getBooleanItem, setBooleanItem } from "@/services/storage";
import { useAudio } from "@/api/useAudio";
import { LoadingScreen } from "@/components/common/Loader/LoadingScreen";
import { TextInputPanel } from "@/components/Chat/TextInputPanel";
import { ChatControlPanel } from "@/components/Chat/ChatControlPanel";
import { VoiceInputPanel } from "@/components/Chat/VoiceInputPanel";
import { BotLoadingMessage } from "@/components/Chat/ChatMessage/BotLoadingMessage";
import { UserLoadingMessage } from "@/components/Chat/ChatMessage/UserLoadingMessage";
import { ChatMessage } from "@/components/Chat/ChatMessage/ChatMessage";
import { useBot } from "@/api/useBot";
import { useChat } from "@/api/useChat";
import { ChatHeader } from "@/components/Chat/ChatHeader";
import { TranslateModal } from "@/components/Chat/TranslateModal";
import { WordModal } from "@/components/Chat/WordModal";
import { GrammarReviewModal } from "@/components/Chat/GrammarReviewModal";
import { ChatControlModal } from "@/components/Chat/ChatControlModal";
import { EVENTS, trackEvent } from "@/services/analytics";
import Link from "next/link";
import { isProfessionalBot } from "@lango/api/src/queries/bot/bot";
import { downloadChatAsPDF } from "@/utils/pdf";

import styles from "./Chat.module.css";
import { VoiceModaDisabledModal } from "./VoiceModaDisabledModal";
import { Button } from "../common/Button";
import { CloseIcon } from "../common/icons/CloseIcon";

const IS_VOICE_MODE_SUPPORTED = true;

export const Chat: React.FC<{ id: string }> = ({ id }) => {
  const [started, setStarted] = useState(false);
  const [voiceModeDisabledModalOpen, setVoiceModeDisabledModalOpen] = useState(
    false
  );
  const [wordModalData, setWordModalData] = useState<string | null>(null);
  const { data: voiceMode, refetch } = useQuery(["voiceMode"], () => {
    if (!IS_VOICE_MODE_SUPPORTED) {
      return false;
    }
    return getBooleanItem("voiceMode") || false;
  });
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const setVoiceMode = useCallback((value: boolean) => {
    if (!IS_VOICE_MODE_SUPPORTED) {
      setVoiceModeDisabledModalOpen(true);
      return;
    }
    setBooleanItem("voiceMode", value);
    refetch();
  }, []);
  const [isControlModalOpen, setControlModalOpen] = useState(false);
  const { playText } = useAudio();
  const { data: bot } = useBot(id);
  const [translateModalOpen, setTranslateModalOpen] = useState(false);
  const [grammarModalData, setGrammarModalData] = useState<{
    message: string;
    prevMessage: string;
  } | null>(null);
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const {
    isPaywallActive,
    onStartConversation,
    messages,
    isLoading,
    isBotThinking,
    isUserThinking,
    retry,
    onVoiceMessage,
    sendMessage,
    onChatSuggestion,
  } = useChat(id);

  useEffect(() => {
    if (grammarModalData) {
      trackEvent(EVENTS.GRAMMAR_OPENED);
    }
  }, [grammarModalData]);

  useEffect(() => {
    if (translateModalOpen) {
      trackEvent(EVENTS.TRANSLATION_OPENED);
    }
  }, [translateModalOpen]);

  useEffect(() => {
    if (wordModalData) {
      trackEvent(EVENTS.WORD_OPENED);
    }
  }, [wordModalData]);

  useEffect(() => {
    if (!bot || isLoading || isPaywallActive || !started) {
      return;
    }
    onStartConversation();
  }, [bot, started, isPaywallActive, isLoading]);

  const onPlayText = useCallback(
    (text: string) => {
      playText(text, bot!.voice);
    },
    [playText, bot]
  );

  const handleTranslation = useCallback(
    (messageId: string, translation: string) => {
      setTranslations((prev) => ({
        ...prev,
        [messageId]: translation,
      }));
    },
    []
  );

  const onDownloadPDF = useCallback(async () => {
    if (messages.length > 0 && bot) {
      await downloadChatAsPDF(messages, bot.name, translations);
      trackEvent(EVENTS.DOWNLOAD_PDF);
    }
  }, [messages, bot, translations]);

  const onNewMessage = useCallback(
    (text: string) => {
      sendMessage({ text, messages, id });
    },
    [sendMessage, messages, id]
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages.length, isBotThinking, isUserThinking]);

  if (isLoading || !bot) {
    return <LoadingScreen />;
  }

  if (!started) {
    const backUrl = bot?.id
      ? isProfessionalBot(bot.id)
        ? "/professional-english"
        : "/"
      : "/";
    return (
      <div className={styles.notStartedContainer}>
        <Link href={backUrl} className={styles.xButton}>
          <CloseIcon />
        </Link>
        <div className={styles.notStarted}>
          <img
            src={`/botsProfiles/${bot.avatar}.png`}
            alt={""}
            className={styles.avatar}
          />
          <div className={styles.name}>{bot.name}</div>
          <div className={styles.description}>{bot.subtitle}</div>
          <Button
            onClick={() => {
              trackEvent(EVENTS.NEW_CHAT);
              setStarted(true);
            }}
            className={styles.startButton}
          >
            Start Chat
          </Button>
        </div>
      </div>
    );
  }

  const lastMessage = messages[messages.length - 1];

  return (
    <div className={styles.container}>
      <div className={styles.messagesWrap}>
        {messages?.slice(-30).map((message, index) => (
          <ChatMessage
            key={message.id}
            data={message}
            onPlayText={onPlayText}
            onWordPress={(word) => {
              setWordModalData(word);
            }}
            onGrammarReview={() => {
              setGrammarModalData({
                message: message.text,
                prevMessage: messages[index - 1]?.text || "",
              });
            }}
            onRetry={retry}
            onTranslation={handleTranslation}
          />
        ))}
        {isUserThinking && <UserLoadingMessage />}
        {isBotThinking && <BotLoadingMessage />}
        <div ref={messagesEndRef} />
      </div>
      {!isPaywallActive && !voiceMode && (
        <TextInputPanel
          disabled={isBotThinking || isUserThinking}
          onNewMessage={onNewMessage}
        >
          <ChatControlPanel
            disabled={isBotThinking || isUserThinking}
            onSuggestion={onChatSuggestion}
            suggestionDisabled={lastMessage?.messageType === "hint"}
            onTranslate={() => setTranslateModalOpen(true)}
          />
        </TextInputPanel>
      )}
      {voiceMode && !isPaywallActive && (
        <VoiceInputPanel
          disabled={isBotThinking || isUserThinking}
          onRecord={onVoiceMessage}
          onMore={() => setControlModalOpen(true)}
          onMic={() => {
            setVoiceMode(!voiceMode);
          }}
        />
      )}
      <ChatHeader
        voiceMode={voiceMode}
        onMic={
          isPaywallActive
            ? undefined
            : () => {
                setVoiceMode(!voiceMode);
              }
        }
        onDownload={messages.length > 0 ? onDownloadPDF : undefined}
      />
      <ChatControlModal
        isOpen={isControlModalOpen}
        onClose={() => setControlModalOpen(false)}
        onSuggestion={onChatSuggestion}
        suggestionDisabled={
          lastMessage?.messageType === "hint" || isBotThinking || isUserThinking
        }
        onTranslate={() => setTranslateModalOpen(true)}
      />
      <TranslateModal
        isOpen={translateModalOpen}
        onClose={() => setTranslateModalOpen(false)}
      />
      <GrammarReviewModal
        message={grammarModalData?.message || ""}
        prevMessage={grammarModalData?.prevMessage || ""}
        isOpen={Boolean(grammarModalData)}
        onClose={() => setGrammarModalData(null)}
      />
      <WordModal
        isOpen={Boolean(wordModalData)}
        onClose={() => setWordModalData(null)}
        word={wordModalData}
      />
      <VoiceModaDisabledModal
        isOpen={voiceModeDisabledModalOpen}
        onClose={() => setVoiceModeDisabledModalOpen(false)}
      />
    </div>
  );
};
